import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import type { User, LoginResponse, MeResponse } from './types';
import { get, post, setIdentityHeaders, clearIdentityHeaders } from './api';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

interface FeaturesState {
  features: string[];
  hasFeature: (key: string) => boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, turnstileToken?: string) => Promise<LoginResponse>;
  verify2fa: (email: string, code: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const FeaturesContext = createContext<FeaturesState>({ features: [], hasFeature: () => false });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true, error: null });
  const [features, setFeatures] = useState<string[]>([]);

  const fetchFeatures = useCallback(async () => {
    try {
      const res = await get<{ features: Array<{ feature_key: string; enabled: number | boolean }> }>('/api/operator/features');
      // D3-01: Backend returns objects {feature_key, enabled}, extract enabled keys
      const enabledKeys = (res.features || [])
        .filter(f => !!f.enabled)
        .map(f => f.feature_key);
      setFeatures(enabledKeys);
    } catch {
      setFeatures([]);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    // Dev-only auth bypass for design-preview workflows.
    // Requires BOTH import.meta.env.DEV (Vite dev) AND explicit opt-in via env var.
    // Never active in production builds. Injects a mock operator so the shell
    // and all protected routes render without a running worker backend.
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === '1') {
      // eslint-disable-next-line no-console
      console.warn('[auth] DEV AUTH BYPASS ACTIVE — mock operator injected (never ship this to prod).');
      const mockUser: User = {
        userId: 'dev-operator',
        email: 'dev@localhost',
        name: 'Dev Operator',
        tenantId: 'dev-tenant',
        role: 'operator',
        is_admin: true,
        permissions: [],
      } as unknown as User;
      setIdentityHeaders({
        'X-Tenant-Id': mockUser.tenantId,
        'X-User-Email': mockUser.email,
        'X-User-Id': mockUser.userId,
      });
      Sentry.setUser({ id: mockUser.userId });
      setState({ user: mockUser, loading: false, error: null });
      setFeatures([
        'command_center:enabled', 'intelligence:enabled', 'customers:enabled',
        'execution:enabled', 'governance:enabled', 'executive:enabled',
        'settings:enabled', 'admin:enabled',
      ]);
      return;
    }
    try {
      const { user } = await get<MeResponse>('/api/auth/me');
      setIdentityHeaders({
        'X-Tenant-Id': user.tenantId,
        'X-User-Email': user.email,
        'X-User-Id': user.userId,
      });
      // PHI-safe: only send opaque user ID to Sentry — never email or tenant details.
      Sentry.setUser({ id: user.userId });
      setState({ user, loading: false, error: null });
      fetchFeatures();
    } catch (err) {
      // On 401: attempt silent refresh using stored refresh token before logging out.
      // This prevents the AUTH_REQUIRED flash on session-exempt routes (e.g. Reports page)
      // when a session expires during normal use.
      const isUnauth = (err as { status?: number })?.status === 401;
      if (isUnauth) {
        const storedRefresh = localStorage.getItem('vv_refresh_token');
        if (storedRefresh) {
          try {
            const refreshRes = await post<LoginResponse>('/api/auth/refresh', { refresh_token: storedRefresh });
            if (refreshRes.success) {
              if (refreshRes.refresh_token) {
                localStorage.setItem('vv_refresh_token', refreshRes.refresh_token);
              }
              // Session renewed — retry fetchMe
              const { user } = await get<MeResponse>('/api/auth/me');
              setIdentityHeaders({
                'X-Tenant-Id': user.tenantId,
                'X-User-Email': user.email,
                'X-User-Id': user.userId,
              });
              Sentry.setUser({ id: user.userId });
              setState({ user, loading: false, error: null });
              fetchFeatures();
              return;
            }
          } catch {
            // Refresh token invalid or expired — fall through to logout
          }
          localStorage.removeItem('vv_refresh_token');
        }
      }
      clearIdentityHeaders();
      Sentry.setUser(null);
      setState({ user: null, loading: false, error: null });
    }
  }, [fetchFeatures]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email: string, password: string, turnstileToken?: string): Promise<LoginResponse> => {
    const res = await post<LoginResponse>('/api/auth/login', { email, password, turnstile_token: turnstileToken });
    if (res.success) {
      if (res.refresh_token) localStorage.setItem('vv_refresh_token', res.refresh_token);
      await fetchMe();
    }
    return res;
  }, [fetchMe]);

  const verify2fa = useCallback(async (email: string, code: string): Promise<LoginResponse> => {
    const res = await post<LoginResponse>('/api/auth/2fa/verify', { email, code });
    if (res.success) {
      if (res.refresh_token) localStorage.setItem('vv_refresh_token', res.refresh_token);
      await fetchMe();
    }
    return res;
  }, [fetchMe]);

  const logout = useCallback(async () => {
    localStorage.removeItem('vv_refresh_token');
    try {
      await post('/api/auth/logout');
    } catch {
      // ignore logout errors
    }
    clearIdentityHeaders();
    Sentry.setUser(null);
    setFeatures([]);
    setState({ user: null, loading: false, error: null });
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const hasFeature = useCallback((key: string) => features.includes(key), [features]);

  return (
    <AuthContext.Provider value={{ ...state, login, verify2fa, logout, refreshUser }}>
      <FeaturesContext.Provider value={{ features, hasFeature }}>
        {children}
      </FeaturesContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useFeatures(): FeaturesState {
  return useContext(FeaturesContext);
}
